import formatDateTime from 'utils/formatDateTime';

function cacheEngine(options = {}) {
	const {
		key = e => e.id,
		filter,
		request,
		extract,
		defaultValue = null,
	} = options;
	const cache = new Map();
	return async (items) => {
		if (!Array.isArray(items)) return [];
		const missing = items.filter(item => filter?.(item) && !cache.has(key(item)));
		if (missing.length) {
			const promise = request(missing);
			missing.forEach(item => cache.set(key(item), promise.then(o => extract(o, item))));
		}
		return Promise.all(items.map(item => cache.get(key(item)) || defaultValue));
	};
}

async function runSingle(item, fn) {
	const data = await fn([item]);
	return data[0];
}

/**
 * Factory function to create movie status utility functions.
 * @param {object} options
 * @param {function} [options.getIsFocus] Function that returns whether the current domain is Focus Features. Defaults to always `false`.
 * @param {function} [options.getRegionCode] Function that returns the current region code. Defaults to returning `__REGION__`.
 * @param {string[]} [options.regionsWithEarlyScreenings] List of region codes where early screenings are common so releases switch status 5pm the day before. Defaults to `['US']`.
 * @returns {object} An object containing getMovieStatus and getAllMovieStatuses functions.
 */
export default function movieStatusUtilsFactory({
	getRegionCode,
	regionsWithEarlyScreenings,
} = {}) {
	const SHOWTIMES_API_ROOT = 'https://showtimes-v2.s-prod.pow.io/v2.0/';

	const getToday = () => formatDateTime(new Date(), 'yyyy-MM-dd');

	const getRegionCodeFn = getRegionCode || (() => __REGION__);
	const regionsWithEarlyScreeningsSet = new Set(regionsWithEarlyScreenings?.map(region => region.toUpperCase()) || ['US']);

	const isComingSoon = movie => {
		const releaseDateStr = movie?.dates?.release;
		if (!releaseDateStr) return true; // If no release date, must be coming soon

		const [year, month, day] = releaseDateStr.split('-');
		if (!year || !month || !day) return true; // Partial date keeps it in coming soon status

		const releaseDate = new Date(year, month - 1, day, 0, 0, 0);
		const regionCode = getRegionCodeFn().toUpperCase();
		if (regionsWithEarlyScreeningsSet.has(regionCode)) {
			// If early screenings are common in this region (i.e. US), release date should be set to 5pm the day before
			releaseDate.setDate(releaseDate.getDate() - 1);
			releaseDate.setHours(17);
		}

		const now = new Date();
		return now < releaseDate;
	};

	// Normalize movie object to ensure consistent access to properties
	const normalizeMovie = movie => {
		return {
			...(movie?.base_movie?.content || {}),
			...(movie.content || {}),
			...movie,
			id: movie.id || movie.movie_id,
		};
	};

	const moviePagesDetails = cacheEngine({
		key: movie => movie?.thundr_id,
		filter: movie => (movie?.id || movie?.movie_id) && movie.thundr_id,
		request: async (missing) => {
			try {
				const url = new URL('universal/movie-pages', SHOWTIMES_API_ROOT);
				const request = await fetch(url.href, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(missing.map(m => ({ id: m.thundr_id }))),
				});
				if (!request.ok) {
					throw new Error(`Failed to fetch movie page data: ${request.statusText}`);
				}
				return await request.json();
			} catch {
				return {};
			}
		},
		extract: (data, movie) => data?.[movie.thundr_id] || null,
	});

	const allShowtimesIds = raw => (Array.isArray(raw) ? raw : raw?.split(',')?.map(id => id?.trim()) || []).filter(Boolean);
	const checkMoviesEarlyScreenings = cacheEngine({
		key: movie => movie.showtimesId,
		filter: movie => (movie?.id || movie?.movie_id) && movie.showtimesId?.length && movie.dates?.release,
		request: async (missing) => {
			try {
				const url = new URL('universal/has-screenings', SHOWTIMES_API_ROOT);
				const region = getRegionCodeFn();
				// Using POST (not semantic) to avoid URL length limits for many movies
				const request = await fetch(url.href, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(
						missing.flatMap(m => allShowtimesIds(m.showtimesId).map(showtimesId => ({
							id: showtimesId?.trim(),
							before: m.dates.release,
							region,
						})))
					),
				});
				if (!request.ok) {
					throw new Error(`Failed to fetch screenings data: ${request.statusText}`);
				}
				return await request.json();
			} catch (error) {
				console.warn('Error fetching early screenings data:', error, missing);
				return {};
			}
		},
		extract: (data, movie) => allShowtimesIds(movie.showtimesId).some(id => data?.[id]),
		defaultValue: false,
	});

	const getMovieStatus = async (movie) => {
		movie = normalizeMovie(movie);

		const status = { theaters: 'unknown', homeEnt: 'unknown' };

		// Unknown status for placeholder movies
		if (movie?.placeholder) return status;

		const releaseDate = movie?.dates?.release;
		const today = getToday();

		const pages = await runSingle(movie, moviePagesDetails);
		// Populate page paths and IDs if available
		if (pages) {
			status.showtimesPath = pages.showtimes?.path;
			status.showtimesId = pages.showtimes?.ids?.[0];
			status.homeEntPath = pages.homeEnt?.path;
		}

		// -- Home Entertainment Status --

		const homeEntDate = movie?.dates?.homeEnt;
		const homeEntPageActive = movie?.pages?.homeEnt?.active;
		// If home entertainment is active and home ent date has passed, set to homeEnt
		if (homeEntPageActive && homeEntDate && today >= homeEntDate) {
			status.homeEnt = 'homeEnt';
		}

		// Skip setting theatrical status if no release date
		if (!releaseDate) return status;

		// -- Theatrical Status --

		const ticketingDate = movie?.dates?.ticketing;
		const showtimesPageActive = movie?.pages?.showtimes?.active;

		// If before release date, set to comingSoon
		if (isComingSoon(movie)) {
			status.theaters = 'comingSoon';

			// If ticketing date has passed and showtimes page is active, check for early screenings
			if (showtimesPageActive && ticketingDate && today >= ticketingDate) {
				const hasEarlyScreenings = await runSingle(movie, checkMoviesEarlyScreenings);
				// If early screenings exist, set to preSale
				if (hasEarlyScreenings) {
					status.theaters = 'preSale';
				}
			}
			// If on/after release date and showtimes page is active, set to inTheaters
		} else if (showtimesPageActive) {
			status.theaters = 'inTheaters';
		}

		return status;
	};

	const getAllMovieStatuses = async (movies) => {
		// Run a single query getting all the necessary movie pages and screening data and cache things
		moviePagesDetails(movies);
		checkMoviesEarlyScreenings(movies.filter(isComingSoon));

		const entries = await Promise.all(movies
			.map(m => normalizeMovie(m))
			.map(async m => m?.id && [m.id, await getMovieStatus(m)]));
		return Object.fromEntries(entries.filter(Boolean));
	};

	return {
		getMovieStatus,
		getAllMovieStatuses,
	};
}